import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useT } from "@/i18n";
import { failText } from "@/lib/failText";
import { shortDay } from "@/lib/format";
import { fetchCrew, setPermanent } from "@/services/crew";
import { ReadOnlyArea } from "@/components/ReadOnly";

// The Responsable's crew: who has worked at this warehouse in the last three weeks, and who of them
// is permanent. Permanent crew are offered first when a job starts. A read-only Supervisor sees the
// list with the boxes greyed; a write the server refuses says why instead of snapping back silently.
export default function CrewList({ warehouseId }: { warehouseId: number | null }) {
  const { t } = useT();
  const qc = useQueryClient();
  const crew = useQuery({ queryKey: ["crew", warehouseId], queryFn: () => fetchCrew(warehouseId!), enabled: !!warehouseId });
  const toggle = useMutation({
    mutationFn: ({ id, permanent }: { id: number; permanent: boolean }) => setPermanent(id, permanent, warehouseId!),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["crew"] }); void qc.invalidateQueries({ queryKey: ["fitters"] }); },
  });

  return (
    <section className="rounded-xl bg-white shadow-sm ring-1 ring-slate-200">
      <h2 className="border-b border-slate-100 px-5 py-3 font-medium text-slate-900">{t("crew.title")}</h2>
      {/* His FIX lists the permanent crew per warehouse: with All chosen there is no one list to tick. */}
      {!warehouseId && <p className="px-5 py-4 text-sm text-slate-500">{t("crew.pickWarehouse")}</p>}
      {warehouseId && crew.data?.crew.length === 0 && <p className="px-5 py-4 text-sm text-slate-400">{t("crew.none")}</p>}
      {crew.isError && <p className="px-5 py-3 text-sm text-red-700">{failText(t, crew.error, "error.loadFailed")}</p>}
      {toggle.isError && (
        <p role="alert" className="mx-5 mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">{failText(t, toggle.error, "job.writeFailed")}</p>
      )}
      <ReadOnlyArea module="supervisor">
        <ul className="divide-y divide-slate-50">
          {crew.data?.crew.map((p) => (
            <li key={p.id} className="flex items-center gap-3 px-5 py-2 text-sm">
              <span className="min-w-0 flex-1 font-medium text-slate-800">{p.first_name}</span>
              <span className="text-xs text-slate-400">{t("crew.lastWorked")} {shortDay(p.last_worked_on)}</span>
              <label className="flex items-center gap-2 text-slate-600">
                <input type="checkbox" checked={p.permanent} disabled={toggle.isPending}
                  onChange={(e) => toggle.mutate({ id: p.id, permanent: e.target.checked })} />
                {t("crew.permanent")}
              </label>
            </li>
          ))}
        </ul>
      </ReadOnlyArea>
      <p className="px-5 py-2 text-xs text-slate-400">{t("crew.hint")}</p>
    </section>
  );
}
